"use client";

import * as amplitude from "@amplitude/analytics-browser";
import { enqueuePendingEvent, getAmplitudeState } from "./client";
import type { EstaidEvent, EventProps } from "./events";

/**
 * The single typed entrypoint for product code emitting Amplitude events.
 *
 * Why this layer exists:
 *   - **Type safety** — `name` and `props` are constrained by `EstaidEvent`,
 *     so a typo in either is a compile error. This keeps the dashboard
 *     coherent: there's no way to ship `track("search subitted", {})` and
 *     have it land as a new noisy event.
 *   - **No-op when disabled** — when init was skipped (local dev, missing
 *     API key, env-disabled), this collapses to a no-op so call sites never
 *     need to gate themselves. Before init and identity are settled, events
 *     are queued instead (see `AmplitudeState` in `./client.ts`).
 *   - **Single seam to mock** — vitest specs that want to assert "this
 *     button fires X" mock this module rather than the entire Amplitude
 *     SDK. Smaller mock surface = less brittle tests.
 *
 * Don't add fallback / retry logic here. Amplitude's transport already
 * handles batching and offline queueing via `localStorage`; piling our
 * own buffer on top would just delay the bug we want to see in alerts.
 */
export function track<N extends EstaidEvent["name"]>(name: N, props: EventProps<N>): void {
    const state = getAmplitudeState();
    if (state === "disabled") return;
    if (state === "ready") {
        amplitude.track(name, props as Record<string, unknown>);
        return;
    }
    enqueuePendingEvent(name, props as Record<string, unknown>);
}
